import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { CachedPage } from './pagination-cache';

/**
 * The reactive state a list consumer renders from. Mirrors the shape a
 * TanStack Query `useQuery` result exposes for a paged list.
 */
export interface ListQueryResult<T> {
  data: T[];
  total: number;
  hasMore: boolean;
  /** No data yet and a fetch is running (show a spinner / skeleton). */
  loading: boolean;
  /** Any fetch is in flight, including a background revalidation. */
  fetching: boolean;
  error: unknown | null;
}

interface ListQueryOptions {
  /** How long a cached entry is served without a background refetch. Default 30s. */
  staleTime?: number;
  /** How long an entry with no subscribers is kept before eviction. Default 5min. */
  gcTime?: number;
}

interface QueryEntry<T> {
  subject: BehaviorSubject<ListQueryResult<T>>;
  fetcher: () => Observable<CachedPage<T>>;
  /** When the data was last fetched. 0 = never fetched or invalidated. */
  storedAt: number;
  inFlight: boolean;
  /** Set if the entry was invalidated while a fetch was in flight, so the
   *  in-flight (now-stale) result triggers one more fetch on completion. */
  dirty: boolean;
  subscribers: number;
  gcTimer?: ReturnType<typeof setTimeout>;
}

function emptyResult<T>(): ListQueryResult<T> {
  return {
    data: [],
    total: 0,
    hasMore: false,
    loading: true,
    fetching: false,
    error: null
  };
}

/**
 * A small keyed, stale-while-revalidate query cache for paged server lists —
 * a lightweight take on TanStack Query for this NgRx codebase.
 *
 * Each distinct `key` owns an independent cache entry, so two views (a full
 * list page and an embedded per-customer list) never clobber each other the
 * way a single shared NgRx collection does. `observe()` returns a live
 * {@link ListQueryResult} stream; on subscribe it serves any fresh cached page
 * instantly and revalidates stale ones in the background. Mutations call
 * {@link invalidate} to mark matching keys stale (refetching the active ones).
 *
 * Server list-state lives here; NgRx keeps genuine client state (selection,
 * drawers, optimistic mutations, entity detail).
 */
@Injectable({ providedIn: 'root' })
export class ListQueryService {
  private readonly entries = new Map<string, QueryEntry<any>>();
  private readonly defaultStaleTime = 30_000;
  private readonly defaultGcTime = 300_000;

  /**
   * Subscribe to the list for `key`, fetching via `fetcher` on a cold or stale
   * entry. The returned stream stays live: pagination/filter changes are a new
   * `key` (call `observe` again — usually via `switchMap`), and a fresh cached
   * page for that key is emitted synchronously.
   */
  observe<T>(
    key: string,
    fetcher: () => Observable<CachedPage<T>>,
    options: ListQueryOptions = {}
  ): Observable<ListQueryResult<T>> {
    const staleTime = options.staleTime ?? this.defaultStaleTime;

    return new Observable<ListQueryResult<T>>(observer => {
      const entry = this.ensureEntry<T>(key, fetcher);
      // Always adopt the latest fetcher closure (captures current auth/env).
      entry.fetcher = fetcher;
      entry.subscribers++;
      if (entry.gcTimer) {
        clearTimeout(entry.gcTimer);
        entry.gcTimer = undefined;
      }

      const age = Date.now() - entry.storedAt;
      if (entry.storedAt === 0) {
        this.runFetch(key); // cold miss
      } else if (age > staleTime) {
        this.runFetch(key); // stale → background revalidate (keeps current data)
      }

      const inner = entry.subject.subscribe(observer);
      return () => {
        inner.unsubscribe();
        entry.subscribers = Math.max(0, entry.subscribers - 1);
        if (entry.subscribers === 0) {
          this.scheduleGc(key, options.gcTime ?? this.defaultGcTime);
        }
      };
    });
  }

  /**
   * Mark every entry whose key matches stale. Active entries (with subscribers)
   * refetch immediately; inactive ones refetch on their next `observe`. Pass a
   * string prefix (e.g. `'consultation|'`) or a predicate.
   */
  invalidate(match: string | ((key: string) => boolean)): void {
    const predicate =
      typeof match === 'string' ? (key: string) => key.startsWith(match) : match;

    for (const [key, entry] of this.entries) {
      if (!predicate(key)) continue;
      entry.storedAt = 0;
      if (entry.subscribers > 0) {
        this.runFetch(key);
      }
    }
  }

  /** Force an immediate refetch of a specific key if it exists. */
  refetch(key: string): void {
    const entry = this.entries.get(key);
    if (!entry) return;
    entry.storedAt = 0;
    this.runFetch(key);
  }

  private ensureEntry<T>(
    key: string,
    fetcher: () => Observable<CachedPage<T>>
  ): QueryEntry<T> {
    let entry = this.entries.get(key) as QueryEntry<T> | undefined;
    if (!entry) {
      entry = {
        subject: new BehaviorSubject<ListQueryResult<T>>(emptyResult<T>()),
        fetcher,
        storedAt: 0,
        inFlight: false,
        dirty: false,
        subscribers: 0
      };
      this.entries.set(key, entry);
    }
    return entry;
  }

  private runFetch(key: string): void {
    const entry = this.entries.get(key);
    if (!entry) return;
    // A fetch is already running: mark the entry dirty so this fetch re-runs
    // once the in-flight (now-stale) one settles, instead of being dropped.
    if (entry.inFlight) {
      entry.dirty = true;
      return;
    }

    entry.inFlight = true;
    entry.dirty = false;
    const current = entry.subject.value;
    entry.subject.next({
      ...current,
      loading: current.data.length === 0,
      fetching: true,
      error: null
    });

    const settle = () => {
      entry.inFlight = false;
      if (entry.dirty) {
        entry.dirty = false;
        this.runFetch(key);
      }
    };

    entry
      .fetcher()
      .pipe(take(1))
      .subscribe({
        next: page => {
          entry.storedAt = Date.now();
          entry.subject.next({
            data: page.data ?? [],
            total: page.totalCount ?? page.data?.length ?? 0,
            hasMore: page.hasMore ?? false,
            loading: false,
            fetching: false,
            error: null
          });
          settle();
        },
        error: error => {
          const value = entry.subject.value;
          entry.subject.next({
            ...value,
            loading: false,
            fetching: false,
            error
          });
          settle();
        }
      });
  }

  private scheduleGc(key: string, gcTime: number): void {
    const entry = this.entries.get(key);
    if (!entry) return;
    if (entry.gcTimer) clearTimeout(entry.gcTimer);
    entry.gcTimer = setTimeout(() => {
      const current = this.entries.get(key);
      if (current && current.subscribers === 0) {
        current.subject.complete();
        this.entries.delete(key);
      }
    }, gcTime);
  }
}
